// Sign PDF (FR-14): tidying signature images and remembering one — only when
// the user ticks the box, and only in this browser's storage.

export type Bounds = { x: number; y: number; width: number; height: number };

/** The smallest box around pixels that aren't see-through, or null if empty. */
export function trimBounds(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  minAlpha = 8,
): Bounds | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (rgba[(y * width + x) * 4 + 3] >= minAlpha) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/** Makes paper-white pixels see-through (for photos of a signature on paper).
 *  Pixels lighter than `threshold` vanish; a band below it fades, so ink edges
 *  stay smooth. Returns a new array. */
export function removeWhiteBackground(
  rgba: Uint8ClampedArray,
  threshold = 200,
  fade = 40,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(rgba);
  for (let i = 0; i < out.length; i += 4) {
    const light = Math.min(out[i], out[i + 1], out[i + 2]);
    if (light >= threshold) out[i + 3] = 0;
    else if (light > threshold - fade) {
      out[i + 3] = Math.round(out[i + 3] * ((threshold - light) / fade));
    }
  }
  return out;
}

export const STORAGE_KEY = "pagekit.signature.v1";
/** Keep stored signatures small (about 750 KB of PNG). */
export const MAX_STORED_CHARS = 1_000_000;
const PREFIX = "data:image/png;base64,";

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Saves a PNG data URL. Returns false if it's too big or storage is blocked. */
export function rememberSignature(store: Store, dataUrl: string): boolean {
  if (!dataUrl.startsWith(PREFIX) || dataUrl.length > MAX_STORED_CHARS)
    return false;
  try {
    store.setItem(STORAGE_KEY, dataUrl);
    return true;
  } catch {
    return false; // private browsing, storage full…
  }
}

/** The remembered signature, or null (also for anything unexpected). */
export function recallSignature(store: Store): string | null {
  try {
    const value = store.getItem(STORAGE_KEY);
    return value && value.startsWith(PREFIX) && value.length <= MAX_STORED_CHARS
      ? value
      : null;
  } catch {
    return null;
  }
}

export function forgetSignature(store: Store) {
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do: storage is unavailable.
  }
}
